import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, MousePointer, Camera, Circle, Square, ChevronLeft, ChevronRight, MessageSquare, MonitorPlay } from 'lucide-react';
import { webrtcManager } from '../services/webrtcManager';
import adminSocketService from '../services/socketService';
import './RemoteViewer.css';

const RemoteViewer = ({ open, onClose, targetSocketId, computerName, onPrev, onNext, onSendMessage }) => {
    const videoRef = useRef(null);
    const [connecting, setConnecting] = useState(true);
    const [error, setError] = useState(null);
    const [stream, setStream] = useState(null);
    const [controlEnabled, setControlEnabled] = useState(false);
    
    // Recording State
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);

    useEffect(() => {
        if (!open || !targetSocketId) return;
        
        setConnecting(true);
        setError(null);
        setStream(null);
        setControlEnabled(false); // Always start in View-Only mode
        
        const unsubscribe = webrtcManager.subscribe(targetSocketId, (newStream, err) => {
            if (err) {
                setError(err);
                setConnecting(false);
                setStream(null);
            } else if (newStream) {
                setStream(newStream);
                setConnecting(false);
                setError(null);
            } else {
                setConnecting(true);
                setStream(null);
            }
        });

        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
            unsubscribe();
        };
    }, [open, targetSocketId]);

    const sendEvent = useCallback((event) => {
        if (!controlEnabled) return;
        const socket = adminSocketService.socket;
        if (socket && targetSocketId) {
            socket.emit('remote-control-event', { targetSocketId, event });
        }
    }, [targetSocketId, controlEnabled]);

    const handleMouseEvent = useCallback((e, type) => {
        if (!videoRef.current) return;
        e.preventDefault();
        const video = videoRef.current;
        const rect = video.getBoundingClientRect();
        
        const videoRatio = video.videoWidth / video.videoHeight;
        const elementRatio = rect.width / rect.height;
        
        let renderedWidth, renderedHeight, offsetX, offsetY;
        
        if (elementRatio > videoRatio) {
            renderedHeight = rect.height;
            renderedWidth = rect.height * videoRatio;
            offsetX = (rect.width - renderedWidth) / 2;
            offsetY = 0;
        } else {
            renderedWidth = rect.width;
            renderedHeight = rect.width / videoRatio;
            offsetX = 0;
            offsetY = (rect.height - renderedHeight) / 2;
        }

        const relativeX = (e.clientX - rect.left) - offsetX;
        const relativeY = (e.clientY - rect.top) - offsetY;

        if (relativeX < 0 || relativeX > renderedWidth || relativeY < 0 || relativeY > renderedHeight) {
            return;
        }

        const scaleX = video.videoWidth / renderedWidth;
        const scaleY = video.videoHeight / renderedHeight;
        const x = Math.round(relativeX * scaleX);
        const y = Math.round(relativeY * scaleY);
        
        const buttonMap = { 0: 'left', 1: 'middle', 2: 'right' };
        const button = buttonMap[e.button] || 'left';
        
        sendEvent({ type, x, y, button });
    }, [sendEvent]);

    useEffect(() => {
        if (!open) return;
        
        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable) return;
            e.preventDefault();
            let key = e.key.toLowerCase();
            if (key === 'control') key = 'control';
            if (key === 'shift') key = 'shift';
            if (key === 'alt') key = 'alt';
            if (key === 'enter') key = 'enter';
            if (key === 'backspace') key = 'backspace';
            if (key === 'delete') key = 'delete';
            if (key === 'escape') key = 'escape';
            if (key === ' ') key = 'space';
            if (key === 'arrowup') key = 'up';
            if (key === 'arrowdown') key = 'down';
            if (key === 'arrowleft') key = 'left';
            if (key === 'arrowright') key = 'right';
            
            sendEvent({ type: 'keydown', key });
        };
        
        const handleKeyUp = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) || e.target.isContentEditable) return;
            e.preventDefault();
            let key = e.key.toLowerCase();
            if (key === ' ') key = 'space';
            if (key === 'arrowup') key = 'up';
            if (key === 'arrowdown') key = 'down';
            if (key === 'arrowleft') key = 'left';
            if (key === 'arrowright') key = 'right';
            
            sendEvent({ type: 'keyup', key });
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [open, sendEvent]);

    const handleTakeScreenshot = () => {
        const video = videoRef.current;
        if (!video) return;
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `Screenshot_${computerName}_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
        link.click();
    };

    const handleToggleRecording = () => {
        if (isRecording) {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
        } else {
            if (!stream) return;
            recordedChunksRef.current = [];
            const options = { mimeType: 'video/webm;codecs=vp9,opus' };
            try {
                mediaRecorderRef.current = new MediaRecorder(stream, options);
            } catch (e) {
                mediaRecorderRef.current = new MediaRecorder(stream);
            }
            
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    recordedChunksRef.current.push(e.data);
                }
            };
            
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Recording_${computerName}_${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;
                link.click();
                URL.revokeObjectURL(url);
            };
            
            mediaRecorderRef.current.start();
            setIsRecording(true);
        }
    };

    if (!open) return null;

    return (
        <div className="rv-overlay">
            <div className="rv-dialog">
                <div className="rv-header">
                    <div className="rv-title-wrap" style={{ flex: 1, minWidth: 0 }}>
                        <h2 className="rv-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Viewing: {computerName}</h2>
                    </div>
                    
                    <div className="rv-actions rv-actions-center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flex: 'auto', flexWrap: 'wrap' }}>
                        {/* 1. Screenshot Button */}
                        <button
                          className="rv-btn"
                          onClick={handleTakeScreenshot}
                          disabled={connecting || !!error}
                          style={{
                            background: 'var(--input-bg)',
                            border: '1px solid var(--border-light)',
                            borderRadius: 24,
                            padding: '10px 18px',
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: (connecting || !!error) ? 'not-allowed' : 'pointer',
                            transition: 'all 200ms',
                            opacity: (connecting || !!error) ? 0.5 : 1
                          }}
                          onMouseEnter={e => { 
                            if (!connecting && !error) {
                              e.currentTarget.style.borderColor = 'var(--accent-primary)'; 
                              e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)'; 
                            }
                          }}
                          onMouseLeave={e => { 
                            if (!connecting && !error) {
                              e.currentTarget.style.borderColor = 'var(--border-light)'; 
                              e.currentTarget.style.background = 'var(--input-bg)'; 
                            }
                          }}
                        >
                          <Camera size={14} color="var(--accent-primary)" /> Screenshot
                        </button>

                        {/* 2. Record Session Button */}
                        <button
                          className="rv-btn"
                          onClick={handleToggleRecording}
                          disabled={connecting || !!error}
                          style={{
                            background: isRecording ? 'rgba(239,68,68,0.1)' : 'var(--input-bg)',
                            border: `1px solid ${isRecording ? '#EF4444' : 'var(--border-light)'}`,
                            borderRadius: 24,
                            padding: '10px 18px',
                            fontSize: 13,
                            fontWeight: 600,
                            color: isRecording ? '#EF4444' : 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: (connecting || !!error) ? 'not-allowed' : 'pointer',
                            transition: 'all 200ms',
                            opacity: (connecting || !!error) ? 0.5 : 1
                          }}
                          onMouseEnter={e => { 
                            if (!connecting && !error) {
                              e.currentTarget.style.borderColor = isRecording ? '#EF4444' : 'var(--accent-primary)'; 
                              e.currentTarget.style.background = isRecording ? 'rgba(239,68,68,0.15)' : 'rgba(var(--app-primary-color-rgb), 0.1)';
                            }
                          }}
                          onMouseLeave={e => { 
                            if (!connecting && !error) {
                              e.currentTarget.style.borderColor = isRecording ? '#EF4444' : 'var(--border-light)'; 
                              e.currentTarget.style.background = isRecording ? 'rgba(239,68,68,0.1)' : 'var(--input-bg)';
                            }
                          }}
                        >
                          <span style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            background: '#EF4444', 
                            display: 'inline-block',
                            animation: isRecording ? 'anim-blink 1s infinite' : 'none' 
                          }} />
                          {isRecording ? 'Recording...' : 'Record Session'}
                        </button>

                        {/* 3. Control PC Button */}
                        <button
                          className="rv-btn"
                          onClick={() => setControlEnabled(!controlEnabled)}
                          disabled={connecting || !!error}
                          style={{
                            background: controlEnabled ? 'var(--accent-gradient)' : 'var(--input-bg)',
                            border: `1px solid ${controlEnabled ? 'var(--accent-primary)' : 'var(--border-light)'}`,
                            borderRadius: 24,
                            padding: '10px 18px',
                            fontSize: 13,
                            fontWeight: 700,
                            color: controlEnabled ? '#FFF' : 'var(--text-primary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: (connecting || !!error) ? 'not-allowed' : 'pointer',
                            transition: 'all 200ms',
                            opacity: (connecting || !!error) ? 0.5 : 1
                          }}
                          onMouseEnter={e => { 
                            if (!controlEnabled && !connecting && !error) {
                              e.currentTarget.style.borderColor = 'var(--accent-primary)'; 
                              e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)';
                            }
                          }}
                          onMouseLeave={e => { 
                            if (!controlEnabled && !connecting && !error) {
                              e.currentTarget.style.borderColor = 'var(--border-light)'; 
                              e.currentTarget.style.background = 'var(--input-bg)';
                            }
                          }}
                        >
                          <MonitorPlay size={14} color={controlEnabled ? '#FFF' : 'var(--accent-primary)'} /> 
                          {controlEnabled ? 'Control Active' : 'Control PC'}
                        </button>

                        {/* 4. Send Message Button */}
                        {onSendMessage && (
                          <button
                            className="rv-btn"
                            onClick={onSendMessage}
                            disabled={connecting || !!error}
                            style={{
                              background: 'var(--input-bg)',
                              border: '1px solid var(--border-light)',
                              borderRadius: 24,
                              padding: '10px 18px',
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              cursor: (connecting || !!error) ? 'not-allowed' : 'pointer',
                              transition: 'all 200ms',
                              opacity: (connecting || !!error) ? 0.5 : 1
                            }}
                            onMouseEnter={e => { 
                              if (!connecting && !error) {
                                e.currentTarget.style.borderColor = 'var(--accent-primary)'; 
                                e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.1)';
                              }
                            }}
                            onMouseLeave={e => { 
                              if (!connecting && !error) {
                                e.currentTarget.style.borderColor = 'var(--border-light)'; 
                                e.currentTarget.style.background = 'var(--input-bg)';
                              }
                            }}
                          >
                            <MessageSquare size={14} color="var(--accent-primary)" /> Send Message
                          </button>
                        )}

                    </div>

                    {/* Right: Disconnect Session Button */}
                    <div className="rv-actions-right" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button 
                          className="rv-btn"
                          onClick={onClose}
                          style={{
                            background: 'rgba(239,68,68,0.1)',
                            border: '1px solid rgba(239,68,68,0.3)',
                            color: '#EF4444',
                            padding: '10px 20px',
                            borderRadius: 24,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 200ms'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                        >
                          Disconnect Session
                        </button>
                    </div>
                </div>
                
                <div className="rv-content">
                    {onPrev && (
                        <button className="rv-nav-btn rv-nav-left" onClick={onPrev}>
                            <ChevronLeft size={32} />
                        </button>
                    )}
                    
                    {onNext && (
                        <button className="rv-nav-btn rv-nav-right" onClick={onNext}>
                            <ChevronRight size={32} />
                        </button>
                    )}

                    {connecting && !error && (
                        <div className="rv-loading">
                            <div className="rv-spinner"></div>
                            <p>Establishing P2P Connection...</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="rv-error">
                            <h3>Connection Failed</h3>
                            <p>{error}</p>
                        </div>
                    )}
                    
                    {stream && !connecting && !error && (
                        <video
                            ref={(el) => {
                                videoRef.current = el;
                                if (el && el.srcObject !== stream) {
                                    el.srcObject = stream;
                                    el.muted = true;
                                    el.defaultMuted = true;
                                    el.setAttribute('playsinline', 'playsinline');
                                    el.setAttribute('webkit-playsinline', 'webkit-playsinline');
                                    el.setAttribute('autoplay', 'autoplay');
                                }
                            }}
                            autoPlay
                            muted
                            playsInline
                            onLoadedMetadata={(e) => {
                                e.target.play().catch(err => console.error("Play error:", err));
                            }}
                            onMouseMove={(e) => handleMouseEvent(e, 'mousemove')}
                            onMouseDown={(e) => handleMouseEvent(e, 'mousedown')}
                            onMouseUp={(e) => handleMouseEvent(e, 'mouseup')}
                            onContextMenu={(e) => e.preventDefault()}
                            className="rv-video"
                            style={{ cursor: controlEnabled ? 'crosshair' : 'default' }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default RemoteViewer;
